import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IAlbum } from '@/data/models/album';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import useGetAllowedSalesTeamsQuery from './useGetAllowedSalesTeamsQuery';
import useGetSalesTeamsQuery from './useGetSalesTeamsQuery';
import { IChangeRequest } from '@/data/models/changeRequest';
import useGetAllowedCRSalesTeamsQuery from '@/hooks/useGetAllowedCRSalesTeamsQuery';

// Interface defining the structure of a single row in the pricing display table
export interface PriceRow {
  salesTeamId?: number;
  type: string;
  name: string;
  currency: string;
  price: number | string;
  section?: PriceRowSection;
  isSeparator?: boolean;
  isCustomPrice?: boolean;
}

export enum PriceRowSection {
  Primary = 'Primary',
  Allowed = 'Allowed',
  Inactive = 'Inactive',
}

// Custom hook to manage state and format data specifically for the Product Pricing tab
export const usePriceTab = (
  album: IAlbum,
  changeRequest: IChangeRequest | undefined
) => {
  const { t } = useTranslation();
  // State for sales teams explicitly allowed for this specific album
  const {
    data: allowedSalesTeams = [],
    isError: isAllowedSalesTeamsError,
    isFetched: allowedSalesTeamsFetched,
  } = changeRequest
    ? useGetAllowedCRSalesTeamsQuery(album.id, changeRequest.id)
    : useGetAllowedSalesTeamsQuery(album.id);

  const { data: allSalesTeams = [], isError: isAllSalesTeamsError } =
    useGetSalesTeamsQuery();

  const error = isAllowedSalesTeamsError || isAllSalesTeamsError;

  const allRows = useMemo(() => {
    // 1. Identify the primary sales team from the album's selected pricing rows
    const primarySalesTeamPrice = album.albumSalesTeams?.find(
      (t) => t.isPrimary
    );
    const primarySequence = primarySalesTeamPrice?.sequence ?? null;
    // Lookup details (name, currency) for the primary team from the allowed or all sales team lists
    const primarySalesTeamDetails = primarySalesTeamPrice
      ? allowedSalesTeams.find(
          (ast) => ast.id === primarySalesTeamPrice.salesTeamId
        ) ||
        allSalesTeams.find(
          (ast) => ast.id === primarySalesTeamPrice.salesTeamId
        )
      : null;

    // 2. Build Section One: Primary Sales Team row
    const sectionOneData: PriceRow[] = [];
    if (primarySalesTeamPrice) {
      const translatedName = primarySalesTeamDetails
        ? t(primarySalesTeamDetails.salesTeamKey)
        : null;
      sectionOneData.push({
        salesTeamId: primarySalesTeamPrice.salesTeamId,
        type: t('product.pricing.salesTerritoritoryTeam'),
        name:
          translatedName ||
          primarySalesTeamPrice.salesTeamName ||
          primarySalesTeamDetails?.salesTeamName ||
          '--',
        currency: primarySalesTeamDetails?.currencyName || '',
        price:
          primarySalesTeamPrice.price !== undefined &&
          primarySalesTeamPrice.price !== null
            ? `${getCurrencySymbol(primarySalesTeamDetails?.currencyName)} ${Number(primarySalesTeamPrice.price).toFixed(2)}`
            : '--',
        section: PriceRowSection.Primary,
      });
    }
    // If there is no primary sales team, push an empty row just to display the section header
    if (sectionOneData.length === 0) {
      sectionOneData.push({
        type: t('product.pricing.salesTerritoritoryTeam'),
        name: '',
        currency: '',
        price: '',
        section: PriceRowSection.Primary,
      });
    }

    // 3. Build Section Two: Allowed Sales Teams (excluding the primary one)
    const allowedNonPrimarySalesTeams = allowedSalesTeams
      .filter((ast) => ast.id !== primarySalesTeamPrice?.salesTeamId)
      .sort((a, b) => t(a.salesTeamKey).localeCompare(t(b.salesTeamKey)));

    const sectionTwoData: PriceRow[] = allowedNonPrimarySalesTeams.map(
      (allowedSalesTeam, index) => {
        // Find the corresponding pricing on the album for this sales team
        const albumSalesTeamPrice = album.albumSalesTeams?.find(
          (t) => t.salesTeamId === allowedSalesTeam.id
        );
        return {
          salesTeamId: allowedSalesTeam.id,
          // Only set the 'type' (section header) for the very first item in this section
          type: index === 0 ? t('product.pricing.salesTerritories') : '',
          name:
            t(allowedSalesTeam.salesTeamKey) || allowedSalesTeam.salesTeamName,
          currency: allowedSalesTeam.currencyName,
          price:
            albumSalesTeamPrice?.price !== undefined &&
            albumSalesTeamPrice?.price !== null
              ? `${getCurrencySymbol(allowedSalesTeam.currencyName)} ${Number(albumSalesTeamPrice.price).toFixed(2)}`
              : '--',
          isCustomPrice:
            albumSalesTeamPrice?.price == null || primarySequence == null
              ? false
              : albumSalesTeamPrice?.isCustomPrice ??
                (albumSalesTeamPrice?.isPrimary
                  ? false
                  : (albumSalesTeamPrice?.sequence ?? null) !==
                    primarySequence),
          section: PriceRowSection.Allowed,
        };
      }
    );
    // If no non-primary allowed sales teams, push an empty row to display the section header
    if (sectionTwoData.length === 0) {
      sectionTwoData.push({
        type: t('product.pricing.salesTerritories'),
        name: '',
        currency: '',
        price: '',
        section: PriceRowSection.Allowed,
      });
    }

    // 4. Build Section Three: Inactive Sales Teams
    // Inactive sales teams are those in `allSalesTeams` but NOT in `allowedSalesTeams`
    const allowedSalesTeamIds = new Set(allowedSalesTeams.map((t) => t.id));

    const inactiveSalesTeams = allSalesTeams
      .filter((t) => !allowedSalesTeamIds.has(t.id))
      .sort((a, b) => t(a.salesTeamKey).localeCompare(t(b.salesTeamKey)));

    const sectionThreeData: PriceRow[] = inactiveSalesTeams.map(
      (salesTeam, index) => ({
        salesTeamId: salesTeam.id,
        // Only set the 'type' (section header) for the very first item in this section
        type: index === 0 ? t('product.pricing.inactiveSalesTerritories') : '',
        name: t(salesTeam.salesTeamKey) || salesTeam.salesTeamName,
        currency: salesTeam.currencyName,
        price: '--',
        section: PriceRowSection.Inactive,
      })
    );
    // If no inactive sales teams, push an empty row to display the section header
    if (sectionThreeData.length === 0) {
      sectionThreeData.push({
        type: t('product.pricing.inactiveSalesTerritories'),
        name: '',
        currency: '',
        price: '',
        section: PriceRowSection.Inactive,
      });
    }

    // 5. Combine the sections into a single array, adding separator rows between sections
    const separatorRow: PriceRow = {
      type: '',
      name: '',
      currency: '',
      price: '',
      isSeparator: true,
    };

    return [
      ...sectionOneData,
      ...(sectionOneData.length > 0 && sectionTwoData.length > 0
        ? [separatorRow]
        : []),
      ...sectionTwoData,
      ...((sectionOneData.length > 0 || sectionTwoData.length > 0) &&
      sectionThreeData.length > 0
        ? [separatorRow]
        : []),
      ...sectionThreeData,
    ];
  }, [album.albumSalesTeams, allowedSalesTeams, allSalesTeams, t]);

  return {
    allRows,
    error,
    allowedSalesTeams,
    allSalesTeams,
    allowedSalesTeamsFetched,
  };
};
